/**
 * Social Media Management - Main Dashboard Component
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  Flex,
  Spacer,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  useToast,
  Spinner,
  Text,
} from '@chakra-ui/react';
import PostsList from './SocialMedia/PostsList';
import PostEditor from './SocialMedia/PostEditor';
import ConnectedAccounts from './SocialMedia/ConnectedAccounts';
import { getPostStats } from '../services/socialMediaService';

interface PostStats {
  total: number;
  drafts: number;
  scheduled: number;
  published: number;
  failed: number;
  byPlatform: {
    facebook: number;
    instagram: number;
    linkedin: number;
    tiktok: number;
  };
}

export default function SocialMediaManagement() {
  const [tabIndex, setTabIndex] = useState(0);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [stats, setStats] = useState<PostStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const toast = useToast();

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const fetchedStats = await getPostStats();
      setStats(fetchedStats);
    } catch (error) {
      console.error('Failed to fetch social media stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load statistics.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingStats(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleEditPost = (postId: string) => {
    setEditingPostId(postId);
    setTabIndex(1); // Switch to the editor tab
  };

  const handleCreateNew = () => {
    setEditingPostId(null); // Clear any existing post for new creation
    setTabIndex(1); // Switch to the editor tab
  };

  const handleEditorCancel = () => {
    setEditingPostId(null);
    setTabIndex(0); // Go back to the list
  };

  const handlePostSaved = () => {
    setEditingPostId(null);
    setTabIndex(0); // Go back to the list
    fetchStats(); // Refresh stats after save
  };

  return (
    <Box p={4}>
      <Flex mb={8} alignItems="center">
        <Heading size="lg" display="flex" alignItems="center">
          📱 Social Media Management
        </Heading>
        <Spacer />
        <Button colorScheme="teal" onClick={handleCreateNew} size="lg">
          + Create New Post
        </Button>
      </Flex>

      {/* Stats Dashboard */}
      {loadingStats ? (
        <Flex justify="center" align="center" height="100px">
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : stats ? (
        <Box mb={8}>
          <StatGroup bg="white" p={5} borderRadius="lg" boxShadow="sm" borderWidth="1px" mb={4}>
            <Stat>
              <StatLabel>Total Posts</StatLabel>
              <StatNumber>{stats.total}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>📝 Drafts</StatLabel>
              <StatNumber>{stats.drafts}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>📅 Scheduled</StatLabel>
              <StatNumber>{stats.scheduled}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>✅ Published</StatLabel>
              <StatNumber>{stats.published}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>❌ Failed</StatLabel>
              <StatNumber color={stats.failed > 0 ? 'red.500' : 'gray.500'}>{stats.failed}</StatNumber>
            </Stat>
          </StatGroup>

          <StatGroup bg="white" p={5} borderRadius="lg" boxShadow="sm" borderWidth="1px">
            <Stat>
              <StatLabel>Facebook</StatLabel>
              <StatNumber color="facebook.500">{stats.byPlatform.facebook}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Instagram</StatLabel>
              <StatNumber color="pink.500">{stats.byPlatform.instagram}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>LinkedIn</StatLabel>
              <StatNumber color="linkedin.500">{stats.byPlatform.linkedin}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>TikTok</StatLabel>
              <StatNumber>{stats.byPlatform.tiktok}</StatNumber>
            </Stat>
          </StatGroup>
        </Box>
      ) : (
        <Text mb={8} color="red.500">Failed to load statistics.</Text>
      )}

      <Tabs index={tabIndex} onChange={setTabIndex} isLazy>
        <TabList>
          <Tab>All Posts</Tab>
          <Tab>{editingPostId ? 'Edit Post' : 'New Post'}</Tab>
          <Tab>⚙️ Connected Accounts</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <PostsList onEdit={handleEditPost} onDataChange={fetchStats} />
          </TabPanel>
          <TabPanel>
            <PostEditor 
              postId={editingPostId} 
              onSave={handlePostSaved} 
              onCancel={handleEditorCancel} 
            />
          </TabPanel>
          <TabPanel>
            <ConnectedAccounts />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
